import { useState } from "react";
import {
  Image,
  Linking,
  Platform,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import {
  Button,
  ThemedText,
  ThemedToast,
  useCustomTheme,
} from "@luna-restaurant/ui";

const EmailIcon = require("@/assets/images/contact.png");

type ContactBannerProps = {
  style?: StyleProp<ViewStyle>;
};

export const ContactBanner = ({ style }: ContactBannerProps) => {
  const { t } = useTranslation();
  const { colors, fonts } = useCustomTheme();
  const [hoveredButton, setHoveredButton] = useState<"email" | "phone" | null>(
    null,
  );

  const emailAddress: string = "info@luna.de";
  const phoneNumber: string = "+49303675950";
  const phoneNumberDisplay: string = "+49 30 3675 950";

  const handleEmailPress = async () => {
    const emailUrl = `mailto:${emailAddress}`;

    if (Platform.OS === "web") {
      window.location.href = emailUrl;
      // Fallback: Copy the email address after a short delay (if no email program is opened)
      setTimeout(async () => {
        try {
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            await navigator.clipboard.writeText(emailAddress);
            ThemedToast.show({
              type: "error",
              text1: t("common.emailFailed"),
              text2: t("common.emailCopied") + "\n" + emailAddress,
            });
          }
        } catch (clipboardError) {
          console.error("Clipboard not available - ignore", clipboardError);
        }
      }, 500);
    } else {
      try {
        await Linking.openURL(emailUrl);
      } catch (error) {
        console.error("Error opeing email URL", error);
        ThemedToast.show({
          type: "error",
          text1: t("common.emailFailed"),
          text2: t("common.emailCopied") + "\n" + emailAddress,
        });
      }
    }
  };

  const handlePhonePress = () => {
    const phoneUrl = `tel:${phoneNumber}`;
    if (Platform.OS === "web") {
      window.location.href = phoneUrl;
    } else {
      Linking.openURL(phoneUrl);
    }
  };

  return (
    <View
      style={[styles.container, style, { backgroundColor: colors.primary }]}
    >
      <View style={styles.contentContainer}>
        <View
          style={[
            styles.iconContainer,
            { backgroundColor: colors.successLighter },
          ]}
        >
          <Image
            source={EmailIcon}
            style={styles.icon}
            tintColor={colors.border}
          />
        </View>
        <View style={styles.textContainer}>
          <ThemedText
            style={[styles.text, { color: colors.border }]}
            type="default"
          >
            {t("common.contactBanner.subtitle")}
          </ThemedText>
          <ThemedText style={[{ color: colors.text }]} type="title">
            {t("common.contactBanner.title")}
          </ThemedText>
        </View>
      </View>
      <View style={styles.buttonContainer}>
        <Button
          buttonText={t("common.contactBanner.email")}
          buttonStyle={[
            styles.button,
            {
              backgroundColor:
                hoveredButton === "email"
                  ? colors.palette.spinach20
                  : colors.border,
            },
          ]}
          buttonTextStyle={[
            styles.buttonText,
            { color: colors.background, ...fonts.bold },
          ]}
          iconName="email-outline"
          iconPosition="left"
          iconSize={24}
          iconColor={colors.background}
          onPress={handleEmailPress}
          onHoverIn={() => setHoveredButton("email")}
          onHoverOut={() =>
            setHoveredButton((current) =>
              current === "email" ? null : current,
            )
          }
        />
        <Button
          buttonText={phoneNumberDisplay}
          buttonStyle={[
            styles.button,
            {
              backgroundColor:
                hoveredButton === "phone"
                  ? colors.palette.spinach20
                  : colors.border,
            },
          ]}
          buttonTextStyle={[
            styles.buttonText,
            { color: colors.background, ...fonts.bold },
          ]}
          iconName="phone"
          iconPosition="left"
          iconSize={24}
          iconColor={colors.background}
          onPress={handlePhonePress}
          onHoverIn={() => setHoveredButton("phone")}
          onHoverOut={() =>
            setHoveredButton((current) =>
              current === "phone" ? null : current,
            )
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 96,
    alignItems: "center",
    gap: 48,
  },
  contentContainer: {
    alignItems: "center",
    gap: 28,
  },
  iconContainer: {
    width: 113,
    height: 113,
    borderRadius: 56.5,
    justifyContent: "center",
  },
  icon: {
    marginLeft: 22,
    marginTop: 4,
    width: 80,
  },
  textContainer: {
    gap: 16,
    alignItems: "center",
  },
  text: {
    fontSize: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 16,
  },
  button: {
    borderRadius: 999,
    height: 56,
    paddingHorizontal: 16,
    gap: 0,
  },
  buttonText: {
    fontSize: 16,
    lineHeight: 20.8,
    paddingHorizontal: 8,
  },
});
